/**
 * Operaciones → Grupos (`/operaciones/grupos`).
 *
 * Puerto de `Groups` + `Groups_model` + `views/groups/{index,create}.php`: el
 * listado de grupos del sistema con su búsqueda y el alta.
 *
 * Un grupo es a la vez **rol** (`admin` y `member`) y **permiso de módulo** (el
 * resto): su nombre es la clave con la que el panel comprueba el acceso
 * (`in_group('configuration')`, `in_group('users')`…) y la que licencian los
 * planes. Por eso el panel anterior no tenía editar ni borrar, y aquí tampoco.
 *
 * Diferencias anotadas:
 * - Se añade el **número de usuarios** de cada grupo (el panel anterior sólo
 *   mostraba el contador total de grupos), para saber a quién afecta.
 * - Pide el grupo `groups`, igual que el panel anterior.
 */

import {
  ArrowLeft,
  Briefcase,
  Plus,
  Search,
  ShieldAlert,
  ShieldCheck,
  UserCog,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber } from '@/lib/utils';

import { useCrearGrupo, useGrupos } from '../grupos.api';
import type { GrupoAmbito, GrupoPayload } from '../grupos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Alta de un grupo. */
function GrupoDialogo({
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: GrupoPayload) => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [ambito, setAmbito] = useState<GrupoAmbito>('administracion');

  const valido = nombre.trim().length > 0 && descripcion.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title="Nuevo grupo"
      description="El nombre del grupo es la clave de acceso que usan las pantallas y los planes: no se podrá cambiar después."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() => onGuardar({ nombre: nombre.trim(), descripcion: descripcion.trim(), ambito })}
          >
            Crear grupo
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo crear">{error}</Alert> : null}

        <Input
          label="Nombre del grupo"
          value={nombre}
          maxLength={20}
          autoFocus
          placeholder="Ej. turn-notes"
          hint="Hasta 20 caracteres, sin repetir otro grupo"
          onChange={(evento) => setNombre(evento.target.value)}
        />

        <Input
          label="Descripción"
          value={descripcion}
          maxLength={100}
          placeholder="Ej. Notas de Venta Turno"
          hint={`${descripcion.length}/100 caracteres`}
          onChange={(evento) => setDescripcion(evento.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-700">Ámbito</span>
          <Select
            value={ambito}
            onChange={(evento) => setAmbito(evento.target.value as GrupoAmbito)}
          >
            <option value="administracion">Administración</option>
            <option value="grifero">Grifero</option>
          </Select>
          <p className="text-xs text-ink-400">
            Columna del formulario de usuarios. Las dos columnas son excluyentes: un usuario no
            puede llevar roles de Administración y de Grifero a la vez.
          </p>
        </div>

        <Alert variant="info">
          Si el grupo es un <strong>permiso de módulo</strong>, recuerda que su nombre debe
          coincidir con el que comprueba el código y con el que declaran los planes y los módulos.
        </Alert>
      </div>
    </Dialog>
  );
}

export function GruposPage() {
  useDocumentTitle('Operaciones · Grupos');

  const grupos = useGrupos();
  const crear = useCrearGrupo();

  const [dialogo, setDialogo] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const sinPermiso = grupos.error instanceof ApiError && grupos.error.status === 403;
  const lista = grupos.data?.grupos ?? [];

  const termino = busqueda.trim().toLowerCase();
  const filtrados = termino
    ? lista.filter(
        (grupo) =>
          grupo.nombre.toLowerCase().includes(termino) ||
          grupo.descripcion.toLowerCase().includes(termino),
      )
    : lista;

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/operaciones"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Operaciones
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Users className="size-6 text-brand-500" aria-hidden="true" />
          Grupos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Roles y permisos de módulo del sistema. Cada grupo es la llave que abre las pantallas de
          EDSuite y la que licencian los planes.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {grupos.data ? (
          <Badge variant="outline">
            {grupos.data.total} grupos · {grupos.data.permisos} permisos de módulo
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo(true);
          }}
        >
          Nuevo grupo
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>groups</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <Input
            leftIcon={<Search className="size-4" aria-hidden="true" />}
            placeholder="Buscar grupos por nombre o descripción…"
            value={busqueda}
            onChange={(evento) => setBusqueda(evento.target.value)}
          />

          {grupos.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : grupos.error ? (
            <Alert variant="error" title="No se pudieron leer los grupos">
              {mensajeDe(grupos.error, 'La API no respondió.')}
            </Alert>
          ) : filtrados.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              {termino
                ? `No hay grupos que coincidan con «${busqueda.trim()}».`
                : 'Todavía no hay grupos registrados.'}
            </Alert>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filtrados.map((grupo) => (
                <div
                  key={grupo.id}
                  className="flex flex-col rounded-card border border-ink-100 bg-white p-3 shadow-soft transition-shadow hover:shadow-card"
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={
                          grupo.tipo === 'tipo'
                            ? 'flex size-8 items-center justify-center rounded-xl bg-rose-50 text-rose-600 ring-1 ring-rose-100'
                            : 'flex size-8 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100'
                        }
                      >
                        {grupo.tipo === 'tipo' ? (
                          <UserCog className="size-4" />
                        ) : (
                          <Briefcase className="size-4" />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-ink-900">
                          {grupo.nombre}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          #{grupo.id} · {grupo.ambito === 'administracion' ? 'Administración' : 'Grifero'}
                        </span>
                      </span>
                    </span>

                    <Badge variant={grupo.tipo === 'tipo' ? 'danger' : 'success'}>
                      {grupo.tipo === 'tipo' ? 'Tipo de usuario' : 'Permiso · módulo'}
                    </Badge>
                  </span>

                  <p className="mt-2 flex-1 text-xs leading-relaxed text-ink-600">
                    {grupo.descripcion || 'Sin descripción'}
                  </p>

                  <span className="mt-2 flex items-center gap-1.5 border-t border-ink-100 pt-2 text-[11px] text-ink-500">
                    <ShieldCheck className="size-3.5 text-ink-400" aria-hidden="true" />
                    {formatNumber(grupo.usuarios)} usuario(s) en el grupo
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Alert variant="info">
        Los grupos <strong>no se editan ni se borran</strong>: su nombre es la clave que comprueban
        el código, los planes y los módulos del sistema. El <strong>ámbito</strong> indica en qué
        columna del formulario de usuarios aparece el grupo (Administración o Grifero). Si un grupo
        ya no se usa, se quita de los usuarios en <strong>Usuarios</strong>.
      </Alert>

      {dialogo ? (
        <GrupoDialogo
          guardando={crear.isPending}
          error={errorDialogo}
          onGuardar={(payload) => {
            setErrorDialogo(null);
            crear.mutate(payload, {
              onSuccess: (datos) => {
                setDialogo(false);
                setAviso(datos.mensaje);
              },
              onError: (fallo) => setErrorDialogo(mensajeDe(fallo, 'No se pudo crear el grupo.')),
            });
          }}
          onCerrar={() => setDialogo(false)}
        />
      ) : null}
    </div>
  );
}
